/**
 * The sandboxed guest document an extension panel runs in.
 *
 * Content policy (26.09.2026, card 39cf5e71). The app's CSP has no
 * 'unsafe-inline' in script-src, and a srcdoc frame inherits its parent's
 * policy, so the guest cannot carry inline code. Instead:
 *
 *  - each piece of guest code (the SDK first, then the extension's entry) is a
 *    non-executable data element whose type is GUEST_SCRIPT_TYPE. The browser
 *    never runs an element of an unknown type and CSP does not apply to it;
 *  - the last element of the body loads ONE fixed, same-origin file,
 *    public/extension-guest-boot.js, by URL. It runs each data element, in
 *    document order, from an object URL.
 *
 * Two policies apply in the guest and both allow exactly that: the inherited
 * app policy (`blob:` plus the boot file by full URL for each packaged origin,
 * see tauri.conf.json) and the guest's own, stricter meta policy below (only
 * the boot file by full URL, and `blob:`). Inline code or inline event
 * handlers in the guest are refused by both.
 *
 * Full URLs, not `'self'`: WebKit (macOS) evaluates the inherited `'self'`
 * against the guest's OPAQUE origin once the guest carries a policy of its
 * own, and refuses the boot file. Chromium does not; both accept the URL.
 *
 * How it is built (29.10.2026). The document is assembled with DOM calls in a
 * detached document (`createHTMLDocument` has no browsing context, so nothing
 * in it can run or load) and serialised once. The output is the same markup
 * the earlier string template produced; this module just no longer writes
 * markup by hand (the hand-written template tripped an antivirus false
 * positive on 28.10.2026 and was quarantined in every Windows checkout).
 */

export const GUEST_SCRIPT_TYPE = 'application/x-apiboo-guest';
export const GUEST_BOOT_FILE = 'extension-guest-boot.js';

/**
 * Absolute URL of the boot file. Absolute on purpose: the guest's origin is
 * opaque (`sandbox` without `allow-same-origin`), so a relative URL or `'self'`
 * in the guest's own policy would not name the app origin.
 */
export function guestBootUrl(origin = window.location.origin, publicUrl = process.env.PUBLIC_URL || '') {
  const base = publicUrl.startsWith('http') ? publicUrl : `${origin}${publicUrl}`;
  return `${base.replace(/\/$/, '')}/${GUEST_BOOT_FILE}`;
}

/** The guest's own CSP. It can only narrow the inherited app policy. */
export function buildGuestCsp(bootUrl) {
  return (
    "default-src 'none'; " +
    `script-src ${bootUrl} blob:; ` +
    "style-src 'unsafe-inline'; img-src data: blob:; connect-src 'none';"
  );
}

function element(doc, tag, attributes, text) {
  const node = doc.createElement(tag);
  for (const [name, value] of attributes) node.setAttribute(name, value);
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * `sources` run in array order (the SDK first, so `window.apibooSDK` exists
 * before the entry reads it). The boot element comes LAST: it only sees the
 * data elements the parser has already built.
 *
 * Returns the srcdoc string. The HTML serialiser writes a script element's
 * text verbatim, exactly as the earlier template concatenated it.
 */
export function buildGuestSrcDoc(sources, bootUrl, doc = document) {
  const guest = doc.implementation.createHTMLDocument('');
  guest.head.replaceChildren(
    element(guest, 'meta', [['charset', 'utf-8']]),
    element(guest, 'meta', [
      ['http-equiv', 'Content-Security-Policy'],
      ['content', buildGuestCsp(bootUrl)],
    ])
  );
  guest.body.replaceChildren(
    ...sources.map((source) => element(guest, 'script', [['type', GUEST_SCRIPT_TYPE]], source)),
    element(guest, 'script', [['src', bootUrl]])
  );
  return `<!DOCTYPE html>${guest.documentElement.outerHTML}`;
}
